// runidea.ai — Phase 5 i18n bootstrap.
// react-i18next config that loads all 10 locale JSONs at build time.
// Language selection cascade:
//   1. logged-in user's `language` preference (set via PATCH /api/auth/me)
//   2. localStorage `runidea.lang`
//   3. navigator.language → first 2/5-char prefix that we support
//   4. fallback: 'en'
// RTL is applied by mutating <html dir> in the language switcher hook.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";
import es from "./locales/es.json";
import ptBR from "./locales/pt-BR.json";
import fr from "./locales/fr.json";
import de from "./locales/de.json";
import it from "./locales/it.json";
import zhCN from "./locales/zh-CN.json";
import ja from "./locales/ja.json";
import ar from "./locales/ar.json";
import hi from "./locales/hi.json";

export const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English", nativeName: "English", dir: "ltr" },
  { code: "es", name: "Spanish", nativeName: "Español", dir: "ltr" },
  { code: "pt-BR", name: "Brazilian Portuguese", nativeName: "Português (Brasil)", dir: "ltr" },
  { code: "fr", name: "French", nativeName: "Français", dir: "ltr" },
  { code: "de", name: "German", nativeName: "Deutsch", dir: "ltr" },
  { code: "it", name: "Italian", nativeName: "Italiano", dir: "ltr" },
  { code: "zh-CN", name: "Simplified Chinese", nativeName: "中文（简体）", dir: "ltr" },
  { code: "ja", name: "Japanese", nativeName: "日本語", dir: "ltr" },
  { code: "ar", name: "Arabic", nativeName: "العربية", dir: "rtl" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी", dir: "ltr" },
];

export const RTL_LANGUAGES = new Set(["ar"]);

const resources = {
  en: { translation: en },
  es: { translation: es },
  "pt-BR": { translation: ptBR },
  fr: { translation: fr },
  de: { translation: de },
  it: { translation: it },
  "zh-CN": { translation: zhCN },
  ja: { translation: ja },
  ar: { translation: ar },
  hi: { translation: hi },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "runidea.lang",
      caches: ["localStorage"],
    },
    interpolation: { escapeValue: false },
    returnObjects: true, // we use arrays for quick_prompts etc.
  });

// Apply <html dir> + <html lang> whenever language changes.
function applyHtmlDir(lng) {
  if (typeof document === "undefined") return;
  const isRtl = RTL_LANGUAGES.has(lng);
  document.documentElement.setAttribute("dir", isRtl ? "rtl" : "ltr");
  document.documentElement.setAttribute("lang", lng);
}
applyHtmlDir(i18n.language);
i18n.on("languageChanged", applyHtmlDir);

export default i18n;
